import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Truck, Camera as CameraIcon, ArrowRight } from "lucide-react";
import LogWizard from "@/components/LogWizard";

const WASTE_TYPES = ["general", "recyclable", "organic", "hazardous"];

export default function Collections() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientMap, setClientMap] = useState({});
  const [siteMap, setSiteMap] = useState({});
  const [streams, setStreams] = useState([]);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("");
  const [streamFilter, setStreamFilter] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardCtx, setWizardCtx] = useState(null);
  const [preview, setPreview] = useState(null);

  const canWrite = user?.role === "admin" || (user?.role === "staff" && user?.capabilities?.includes("collections.write"));

  const load = () => api.get("/collections").then((r) => setItems(r.data));
  const loadPending = () => {
    if (!canWrite) return;
    api.get("/collections/pending-jobs").then((r) => setPendingJobs(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    loadPending();
    api.get("/clients").then((r) => {
      setClients(r.data);
      const m = {}; r.data.forEach((c) => (m[c.id] = c.name)); setClientMap(m);
    });
    api.get("/sites").then((r) => {
      const m = {}; r.data.forEach((s) => (m[s.id] = s.name)); setSiteMap(m);
    });
    api.get("/waste-catalog/streams").then((r) => setStreams(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const openBlankWizard = () => { setWizardCtx(null); setWizardOpen(true); };
  const openJobWizard = (job) => { setWizardCtx({ jobContext: job }); setWizardOpen(true); };

  const remove = async (c) => {
    if (!window.confirm("Delete this collection?")) return;
    await api.delete(`/collections/${c.id}`);
    toast.success("Deleted"); load();
  };

  const uniqueStreamsInLog = useMemo(() => {
    const map = new Map();
    items.forEach((i) => { if (i.waste_stream_name) map.set(i.waste_stream_id || i.waste_stream_name, i.waste_stream_name); });
    return Array.from(map.entries());
  }, [items]);

  const filtered = items
    .filter((i) => typeFilter === "all" || i.waste_type === typeFilter)
    .filter((i) => !siteFilter || i.site_id === siteFilter)
    .filter((i) => !streamFilter || (i.waste_stream_id || i.waste_stream_name) === streamFilter);

  return (
    <div>
      <PageHeader
        eyebrow="Data capture"
        title={user?.role === "client" ? "My Collections" : "Collections"}
        subtitle={user?.role === "client" ? "Every pickup and drop-off — searchable by waste stream." : "Log real weights against completed pickups. The batch wizard lets you enter every stream in one go."}
        actions={canWrite && (
          <button onClick={openBlankWizard} className="btn-primary inline-flex items-center gap-2" data-testid="add-collection-btn">
            <Plus className="w-4 h-4" /> Log a collection
          </button>
        )}
      />
      <div className="px-8 lg:px-12 py-8 space-y-6">

        {/* Pending Data Capture queue */}
        {canWrite && pendingJobs.length > 0 && (
          <section data-testid="pending-jobs-section">
            <div className="hbt-label mb-3">Pending data capture · {pendingJobs.length}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {pendingJobs.map((job) => (
                <div key={`${job.trip_id}-${job.stop_index}`} className="hbt-card p-4" data-testid={`pending-job-${job.trip_id}-${job.stop_index}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="hbt-label">{job.trip_number}</div>
                      <div className="font-display font-semibold mt-0.5">{clientMap[job.client_id] || "Unknown client"}</div>
                      <div className="text-xs text-neutral-500 font-mono-num mt-1">{job.trip_date} · {job.driver} · {job.vehicle}</div>
                    </div>
                    <Truck className="w-5 h-5 text-neutral-400" />
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {job.photo_before ? <img src={job.photo_before} alt="before" className="w-full h-20 object-cover rounded" /> : <div className="h-20 rounded flex items-center justify-center text-[10px] text-neutral-400 bg-neutral-50"><CameraIcon className="w-4 h-4" /></div>}
                    {job.photo_after ? <img src={job.photo_after} alt="after" className="w-full h-20 object-cover rounded" /> : <div className="h-20 rounded flex items-center justify-center text-[10px] text-neutral-400 bg-neutral-50"><CameraIcon className="w-4 h-4" /></div>}
                  </div>
                  <button onClick={() => openJobWizard(job)} className="btn-primary w-full inline-flex items-center justify-center gap-2 text-sm" data-testid={`log-job-${job.trip_id}-${job.stop_index}`}>
                    Log streams <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="hbt-input max-w-[180px] text-sm" data-testid="type-filter">
            <option value="all">All waste types</option>
            {WASTE_TYPES.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
          </select>
          <select value={streamFilter} onChange={(e) => setStreamFilter(e.target.value)} className="hbt-input max-w-[220px] text-sm" data-testid="stream-filter">
            <option value="">All streams</option>
            {uniqueStreamsInLog.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <div className="text-xs text-neutral-500 ml-auto">{filtered.length} of {items.length}</div>
        </div>

        {/* Table */}
        <div className="hbt-card overflow-hidden">
          <table className="hbt-table">
            <thead>
              <tr>
                <th>Date</th>
                {user?.role !== "client" && <th>Client</th>}
                <th>Site</th>
                <th>Stream</th>
                <th>Treatment</th>
                <th>Vehicle</th>
                <th>Driver</th>
                <th className="num">Quantity</th>
                <th>Evidence</th>
                {canWrite && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const unit = c.unit || "kg";
                const qty = c.quantity ?? c.weight_kg;
                return (
                  <tr key={c.id} data-testid={`collection-row-${c.id}`}>
                    <td className="font-mono-num">{c.collection_date}</td>
                    {user?.role !== "client" && <td>{clientMap[c.client_id] || "—"}</td>}
                    <td className="text-neutral-600 text-xs">{siteMap[c.site_id] || <span className="text-neutral-400">—</span>}</td>
                    <td>
                      <div className="text-sm">{c.waste_stream_name || <span className="text-neutral-400 capitalize">{c.waste_type}</span>}</div>
                      <span className={`pill text-[10px] ${c.waste_type === "recyclable" ? "forest" : c.waste_type === "hazardous" ? "error" : c.waste_type === "organic" ? "success" : ""}`}>{c.waste_type}</span>
                    </td>
                    <td className="text-neutral-600 text-sm">{c.treatment_name || <span className="capitalize">{c.disposal_method}</span>}</td>
                    <td className="text-neutral-600">{c.vehicle || "—"}</td>
                    <td className="text-neutral-600">{c.driver || "—"}</td>
                    <td className="num font-mono-num font-semibold">{Number(qty || 0).toFixed(1)} {unit}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        {c.photo_before && <button onClick={() => setPreview(c.photo_before)} className="w-8 h-8 rounded overflow-hidden border" style={{ borderColor: "var(--border)" }} title="Before"><img src={c.photo_before} alt="" className="w-full h-full object-cover" /></button>}
                        {c.photo_after && <button onClick={() => setPreview(c.photo_after)} className="w-8 h-8 rounded overflow-hidden border" style={{ borderColor: "var(--border)" }} title="After"><img src={c.photo_after} alt="" className="w-full h-full object-cover" /></button>}
                        {!c.photo_before && !c.photo_after && <span className="text-xs text-neutral-400">—</span>}
                      </div>
                    </td>
                    {canWrite && (
                      <td>
                        <div className="flex items-center gap-2">
                          <button onClick={() => remove(c)} className="text-xs text-neutral-500 hover:text-red-700" data-testid={`delete-collection-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="text-center text-neutral-400 py-12">{items.length === 0 ? "No collections logged yet." : "No collections match this filter."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {wizardOpen && (
        <LogWizard
          initial={wizardCtx}
          onClose={() => setWizardOpen(false)}
          onSaved={() => { load(); loadPending(); }}
        />
      )}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={() => setPreview(null)}>
          <img src={preview} alt="" className="max-w-[80vw] max-h-[80vh] rounded" />
        </div>
      )}
    </div>
  );
}
